// Mock data for Arjun Bhusal's AI/ML Portfolio

export const personalInfo = {
  name: "Arjun Bhusal",
  title: "AI/ML Engineer & Full-Stack Developer",
  email: "arjunbhusal684@gmail.com",
  phone: "+977-9866695327",
  github: "https://github.com/Buzzingaa",
  linkedin: "https://linkedin.com/in/arjun-bhusal-",
  location: "Nepal",
  bio: "Result-driven Computer Engineer with hands-on experience in machine learning, deep learning, and full-stack development. Strong track record delivering research-grade models and production-focused projects (computer vision, NLP, GANs). Skilled in Python, TensorFlow, PyTorch, and modern web frameworks."
};

export const skills = {
  languages: ["Python", "C/C++", "JavaScript", "SQL"],
  frameworks: ["TensorFlow", "PyTorch", "scikit-learn", "OpenCV", "Transformers"],
  webTools: ["React.js", "FastAPI", "Django", "HTML/CSS", "Git", "GitHub", "Redis", "Swagger"],
  mlTopics: ["CNN", "RNN/LSTM", "Attention & Transformers", "GANs", "Transfer Learning", "RAG", "Model Evaluation", "Multi-label Classification"]
};

export const education = {
  degree: "Bachelor of Engineering (B.E.), Computer Engineering",
  institution: "Institute of Engineering – Paschimanchal Campus, Nepal",
  duration: "Dec 2019 – Apr 2024",
  coursework: [
    "Data Structures & Algorithms",
    "Machine Learning", 
    "Image Processing",
    "Distributed Systems",
    "Databases",
    "Operating Systems",
    "Computer Networks",
    "Big Data Technologies"
  ]
};

export const projects = [
  {
    id: 1,
    title: "Football Highlights Generation",
    period: "2023–2024",
    description: "Led design and implementation of an automated highlight-generation pipeline for full-length football matches using cascaded classifiers and computer vision.",
    highlights: [
      "Built cascaded classifier combining custom VGG-16 CNN and audio-intensity analysis",
      "Integrated YOLOv8 for on-field object detection",
      "User-configurable highlight length with MoviePy post-processing"
    ],
    tech: ["Python", "TensorFlow/Keras", "VGG-16", "YOLOv8", "ResNet", "MoviePy"],
    metrics: {
      accuracy: "92%",
      processingTime: "5x faster than manual",
      eventTypes: "5 categories"
    },
    hasDemo: true,
    hasNotebook: true,
    githubUrl: "https://github.com/Buzzingaa/football-highlights",
    featured: true
  },
  {
    id: 2,
    title: "Generative Adversarial Network — Fashion Image Synthesis",
    period: "2024",
    description: "Implemented custom Generator and Discriminator networks with tailored training loop for GAN stability on Fashion MNIST dataset.",
    highlights: [
      "Custom Generator and Discriminator architecture design",
      "Tailored training loop for GAN stability",
      "High-fidelity clothing image synthesis"
    ],
    tech: ["TensorFlow", "NumPy", "Matplotlib", "Pandas"],
    metrics: {
      fid: "38.2",
      epochs: "100",
      imageQuality: "High-fidelity"
    },
    hasDemo: false,
    hasNotebook: true,
    githubUrl: "https://github.com/Buzzingaa/fashion-gan",
    featured: true
  },
  {
    id: 3,
    title: "Celebrity Face Recognition",
    period: "2024",
    description: "Built a face recognition pipeline using OpenCV for preprocessing and SVM for classification with hyperparameter tuning.",
    highlights: [
      "OpenCV-based preprocessing and feature extraction",
      "SVM classification with GridSearchCV tuning",
      "Superior generalization over Random Forest and Logistic Regression"
    ],
    tech: ["OpenCV", "scikit-learn", "SVM", "GridSearchCV", "NumPy", "Pandas"],
    metrics: {
      trainAccuracy: "97%",
      valAccuracy: "95%",
      features: "2048 dimensions"
    },
    hasDemo: false,
    hasNotebook: true,
    githubUrl: "https://github.com/Buzzingaa/celebrity-face-recognition",
    featured: false
  },
  {
    id: 4,
    title: "Crop Disease Classification",
    period: "2023",
    description: "Trained ResNet50 model on multi-class plant disease dataset to identify leaf diseases with transfer learning.",
    highlights: [
      "ResNet50 transfer learning implementation",
      "38-class disease classification",
      "Data augmentation and model optimization"
    ],
    tech: ["TensorFlow", "ResNet50", "scikit-learn", "Pandas"],
    metrics: {
      trainAccuracy: "95%",
      valAccuracy: "94%",
      classes: "38 diseases"
    },
    hasDemo: false,
    hasNotebook: true,
    githubUrl: "https://github.com/Buzzingaa/crop-disease-classification",
    featured: true
  },
  {
    id: 5,
    title: "Bigya — Expert Discovery Platform",
    period: "2022",
    description: "Contributed to full-stack experts-hiring web platform with search, reviews, ratings and efficient caching.",
    highlights: [
      "Full-stack web platform development",
      "Efficient search and Redis caching implementation",
      "REST API documentation with Swagger"
    ],
    tech: ["Django", "React", "MySQL", "Redis", "RESTful APIs"],
    metrics: {
      responseTime: "<200ms",
      cacheHitRate: "85%",
      apiEndpoints: "25+"
    },
    hasDemo: false,
    hasNotebook: false,
    githubUrl: "https://github.com/Buzzingaa/bigya-platform",
    featured: false
  }
];

export const certifications = [
  {
    title: "Neural Networks & Deep Learning",
    issuer: "DeepLearning.AI",
    date: "Nov 2023",
    credentialUrl: "#"
  },
  {
    title: "Advanced Learning Algorithms",
    issuer: "DeepLearning.AI", 
    date: "July 2023",
    credentialUrl: "#"
  },
  {
    title: "Supervised Machine Learning: Regression and Classification",
    issuer: "DeepLearning.AI",
    date: "July 2023", 
    credentialUrl: "#"
  },
  {
    title: "Structuring Machine Learning Projects",
    issuer: "DeepLearning.AI",
    date: "Feb 2024",
    credentialUrl: "#"
  }
];

export const blogPosts = [
  {
    id: 1,
    title: "Building an Automated Football Highlights System: A Deep Dive",
    excerpt: "Learn how I built an end-to-end pipeline combining computer vision, audio analysis, and deep learning to automatically generate football highlights from full matches.",
    content: `# Building an Automated Football Highlights System: A Deep Dive

Creating an automated football highlights system was one of my most challenging and rewarding projects. Here's how I approached this complex computer vision problem.

## The Challenge

Manual highlight creation is time-consuming and subjective. I wanted to build a system that could automatically detect and extract key moments from 90-minute football matches.

## Architecture Overview

The system uses a cascaded approach:

1. **Frame-level Event Detection**: Custom VGG-16 CNN trained to classify individual frames
2. **Audio Intensity Analysis**: EMA (Exponential Moving Average) to detect crowd excitement
3. **Object Detection**: YOLOv8 for on-field player and ball tracking
4. **Post-processing**: MoviePy for smooth clipping and transitions

## Key Technical Decisions

### Why VGG-16 over ResNet?
After testing both architectures, VGG-16 showed better performance for frame-level classification due to its simpler architecture being less prone to overfitting on our dataset.

### Cascaded Classification Approach
Instead of relying on a single model, I used multiple signals:
- Visual features (goals, cards, celebrations)
- Audio features (crowd noise, referee whistles)
- Temporal context (game flow, timing)

## Results

The system achieved 92% accuracy in detecting key events and reduced manual highlight creation time by 5x.

## Lessons Learned

1. Multi-modal approaches often outperform single-modal solutions
2. Domain-specific preprocessing is crucial for sports video analysis
3. Post-processing quality significantly impacts user experience

The code and trained models are available on my GitHub for anyone interested in sports analytics!`,
    author: "Arjun Bhusal",
    publishedAt: "2024-01-15",
    readTime: "8 min read",
    tags: ["Computer Vision", "Deep Learning", "Sports Analytics"],
    featured: true
  },
  {
    id: 2,
    title: "Transfer Learning with ResNet50: Lessons from Crop Disease Classification",
    excerpt: "A practical guide to implementing transfer learning for agricultural applications, including data preparation, model fine-tuning, and evaluation strategies.",
    content: `# Transfer Learning with ResNet50: Lessons from Crop Disease Classification

Transfer learning has revolutionized how we approach computer vision problems, especially when working with limited datasets. Here's what I learned building a crop disease classifier.

## The Problem

Plant diseases cause significant crop losses worldwide. Early detection through image analysis can help farmers take preventive measures.

## Dataset Challenges

Working with agricultural data presents unique challenges:
- Class imbalance (some diseases are rarer)
- Lighting variations (field conditions)
- Background noise (soil, other plants)

## Transfer Learning Strategy

### Model Selection
I chose ResNet50 for several reasons:
- Pre-trained on ImageNet provides good feature representations
- Appropriate depth for our complexity level
- Well-documented and stable architecture

### Fine-tuning Approach
1. **Freeze early layers**: Keep low-level feature extractors unchanged
2. **Fine-tune top layers**: Adapt high-level features to agricultural domain
3. **Gradual unfreezing**: Slowly unlock more layers as training progresses

## Data Preparation

The key to success was comprehensive data augmentation:
- Rotation and flipping (plants can be photographed from any angle)
- Color jittering (different lighting conditions)
- Random crops (focus on diseased areas)

## Results and Insights

Achieved 94% validation accuracy on 38 disease classes. Key insights:

1. **Domain adaptation matters**: Agricultural images have different characteristics than ImageNet
2. **Data quality > quantity**: Clean, well-labeled samples beat large noisy datasets
3. **Validation strategy**: Use temporal splits to avoid data leakage

## Practical Tips

- Start with a small learning rate for fine-tuning
- Monitor both training and validation metrics closely
- Use class weights for imbalanced datasets
- Implement proper cross-validation for agricultural data

This project taught me that successful transfer learning requires understanding both the source and target domains deeply.`,
    author: "Arjun Bhusal",
    publishedAt: "2024-02-10",
    readTime: "6 min read",
    tags: ["Transfer Learning", "Agriculture", "Computer Vision"],
    featured: false
  },
  {
    id: 3,
    title: "5 Key Lessons from My First GAN Implementation",
    excerpt: "Implementing Generative Adversarial Networks taught me valuable lessons about training instability, architecture design, and evaluation metrics. Here's what I learned.",
    content: `# 5 Key Lessons from My First GAN Implementation

Building my first GAN was a humbling experience. Here are the key lessons that would have saved me weeks of debugging.

## Lesson 1: Training Stability is Everything

GANs are notoriously difficult to train. My first attempts resulted in mode collapse and vanishing gradients.

**Solution**: Implement proper loss functions and learning rate schedules. I found that using different learning rates for generator and discriminator helped stabilize training.

## Lesson 2: Architecture Balance Matters

The generator and discriminator need to be evenly matched. If one becomes too powerful, training fails.

**My approach**:
- Started with symmetric architectures
- Monitored loss ratios during training
- Adjusted capacity when one network dominated

## Lesson 3: Evaluation is Challenging

Traditional metrics don't work well for GANs. How do you measure "image quality"?

**Metrics I used**:
- Fréchet Inception Distance (FID)
- Visual inspection at regular intervals
- Mode coverage analysis

## Lesson 4: Batch Size and Normalization

Small details have huge impacts in GAN training:
- Batch normalization in generator, no BN in discriminator final layer
- Larger batch sizes improve stability
- Spectral normalization helps with gradient flow

## Lesson 5: Patience and Experimentation

GANs require extensive hyperparameter tuning. What worked:
- Start simple, add complexity gradually
- Save checkpoints frequently
- Track experiments systematically

## Final Thoughts

My Fashion-MNIST GAN finally achieved decent results after understanding these principles. The key is treating GAN training as a delicate balance rather than standard supervised learning.

The journey taught me more about deep learning fundamentals than any other project. Sometimes the hardest problems provide the best learning experiences!`,
    author: "Arjun Bhusal",
    publishedAt: "2024-03-05",
    readTime: "5 min read",
    tags: ["GANs", "Deep Learning", "Lessons Learned"],
    featured: false
  }
];

export const codeSnippets = {
  footballHighlights: `# Football Highlights - Event Detection Pipeline
import tensorflow as tf
from tensorflow.keras.applications import VGG16
import cv2
import numpy as np

class EventDetector:
    def __init__(self):
        # Load pre-trained VGG16 for feature extraction
        self.base_model = VGG16(weights='imagenet', 
                               include_top=False, 
                               input_shape=(224, 224, 3))
        
        # Custom classification head
        self.model = self._build_classifier()
        
    def _build_classifier(self):
        model = tf.keras.Sequential([
            self.base_model,
            tf.keras.layers.GlobalAveragePooling2D(),
            tf.keras.layers.Dense(512, activation='relu'),
            tf.keras.layers.Dropout(0.5),
            tf.keras.layers.Dense(5, activation='softmax')  # 5 event types
        ])
        return model
    
    def detect_events(self, video_path):
        cap = cv2.VideoCapture(video_path)
        events = []
        
        while cap.isOpened():
            ret, frame = cap.read()
            if not ret:
                break
                
            # Preprocess frame
            frame_resized = cv2.resize(frame, (224, 224))
            frame_normalized = frame_resized / 255.0
            
            # Predict event type
            prediction = self.model.predict(
                np.expand_dims(frame_normalized, axis=0)
            )
            
            event_type = np.argmax(prediction)
            confidence = np.max(prediction)
            
            if confidence > 0.8:  # High confidence threshold
                timestamp = cap.get(cv2.CAP_PROP_POS_MSEC) / 1000
                events.append({
                    'timestamp': timestamp,
                    'event': event_type,
                    'confidence': confidence
                })
        
        cap.release()
        return events`,
        
  cropDisease: `# Crop Disease Classification - ResNet50 Transfer Learning
import tensorflow as tf
from tensorflow.keras.applications import ResNet50
from tensorflow.keras.preprocessing.image import ImageDataGenerator

class CropDiseaseClassifier:
    def __init__(self, num_classes=38):
        self.num_classes = num_classes
        self.model = self._build_model()
        
    def _build_model(self):
        # Load ResNet50 pre-trained on ImageNet
        base_model = ResNet50(weights='imagenet',
                             include_top=False,
                             input_shape=(224, 224, 3))
        
        # Freeze early layers
        for layer in base_model.layers[:-10]:
            layer.trainable = False
            
        # Add custom classification head
        model = tf.keras.Sequential([
            base_model,
            tf.keras.layers.GlobalAveragePooling2D(),
            tf.keras.layers.Dense(512, activation='relu'),
            tf.keras.layers.BatchNormalization(),
            tf.keras.layers.Dropout(0.5),
            tf.keras.layers.Dense(256, activation='relu'),
            tf.keras.layers.Dropout(0.3),
            tf.keras.layers.Dense(self.num_classes, activation='softmax')
        ])
        
        return model
    
    def prepare_data(self, train_dir, val_dir):
        # Data augmentation for training
        train_datagen = ImageDataGenerator(
            rescale=1./255,
            rotation_range=20,
            width_shift_range=0.2,
            height_shift_range=0.2,
            horizontal_flip=True,
            zoom_range=0.2,
            brightness_range=[0.8, 1.2]
        )
        
        val_datagen = ImageDataGenerator(rescale=1./255)
        
        train_generator = train_datagen.flow_from_directory(
            train_dir,
            target_size=(224, 224),
            batch_size=32,
            class_mode='categorical'
        )
        
        val_generator = val_datagen.flow_from_directory(
            val_dir,
            target_size=(224, 224),
            batch_size=32,
            class_mode='categorical'
        )
        
        return train_generator, val_generator
    
    def train(self, train_gen, val_gen, epochs=50):
        # Compile with appropriate optimizer
        self.model.compile(
            optimizer=tf.keras.optimizers.Adam(learning_rate=0.001),
            loss='categorical_crossentropy',
            metrics=['accuracy']
        )
        
        # Callbacks
        callbacks = [
            tf.keras.callbacks.ReduceLROnPlateau(patience=5),
            tf.keras.callbacks.EarlyStopping(patience=10),
            tf.keras.callbacks.ModelCheckpoint('best_model.h5', 
                                             save_best_only=True)
        ]
        
        # Train the model
        history = self.model.fit(
            train_gen,
            validation_data=val_gen,
            epochs=epochs,
            callbacks=callbacks
        )
        
        return history`
};